import {useLocalStorageState} from 'hooks/useLocalStorageState'
import React, {useContext, useEffect} from 'react'
import {Ventrata} from 'types'
import {useOrderDetailsMutators} from './OrderDetailsProvider'

type Props = {
  children: React.ReactNode
}

type OrderValue = {
  order: Ventrata.Order | null
  setOrder: (order: Ventrata.Order | null) => Ventrata.Order | null
}

const validator = (data: unknown) => data === null || typeof data === 'object'

export const OrderContext = React.createContext<OrderValue>({
  order: null,
  setOrder: () => null,
})

export const OrderProvider = ({children}: Props) => {
  const {reset} = useOrderDetailsMutators()

  const [order, setOrder] = useLocalStorageState<Ventrata.Order | null>({
    defaultValue: null,
    entity: 'order',
    validator,
  })

  const value = {
    order,
    setOrder,
  }

  useEffect(() => {
    if (order && order.bookings.length === 0) {
      setOrder(null)
    }

    document.dispatchEvent(new CustomEvent('CheckoutCartChanged', {detail: {bookings: order?.bookings.length ?? 0}}))
  }, [order, setOrder])

  useEffect(() => {
    if (!order) {
      reset()
    }
  }, [order, reset])

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
}

function useOrder(): Ventrata.Order
function useOrder(mode: 'required'): Ventrata.Order
function useOrder(mode: 'optional'): Ventrata.Order | null
function useOrder(mode?: 'required' | 'optional') {
  const {order} = useContext(OrderContext)

  if (mode === 'optional') {
    return order
  }

  if (!order) {
    throw new Error('Order is not defined')
  }

  return order
}

OrderProvider.useOrder = useOrder

OrderProvider.useOrderSetter = () => {
  const {setOrder} = useContext(OrderContext)
  return setOrder
}

OrderProvider.useBooking = (uuid: string) => {
  const order = useOrder()

  const booking = order.bookings.find((booking) => booking.uuid === uuid)

  if (!booking) {
    throw new Error(`Can't find booking by uuid '${uuid}'`)
  }

  return booking
}
